import { useEffect, useState } from 'react';

const TAB_ID_KEY = 'tabId';
const ACTIVE_TAB_ID_KEY = 'activeTabId';
const HEARTBEAT_KEY = 'activeTabHeartbeat';

const HEARTBEAT_INTERVAL = 3000; // 3 seconds
const STALE_THRESHOLD = 5000; // 5 seconds

export function useMultiTabDetection() {
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    // Generate or retrieve tabId (tab-specific)
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = crypto.randomUUID();
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }

    const activeTabId = localStorage.getItem(ACTIVE_TAB_ID_KEY);
    const heartbeat = localStorage.getItem(HEARTBEAT_KEY);

    const now = Date.now();
    const isHeartbeatStale =
      !heartbeat || now - Number(heartbeat) > STALE_THRESHOLD;

    // If another active tab exists and heartbeat is fresh → block
    if (activeTabId && activeTabId !== tabId && !isHeartbeatStale) {
      setIsBlocked(true);
      return;
    }

    // Otherwise, take ownership
    localStorage.setItem(ACTIVE_TAB_ID_KEY, tabId);
    localStorage.setItem(HEARTBEAT_KEY, now.toString());

    // Heartbeat updater
    const interval = setInterval(() => {
      localStorage.setItem(HEARTBEAT_KEY, Date.now().toString());
    }, HEARTBEAT_INTERVAL);

    // Listen to other tabs logging in/out
    const onStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVE_TAB_ID_KEY && e.newValue && e.newValue !== tabId) {
        setIsBlocked(true);
      }
    };

    window.addEventListener('storage', onStorageChange);

    // Cleanup on tab close
    const onBeforeUnload = () => {
      const currentActiveTab = localStorage.getItem(ACTIVE_TAB_ID_KEY);
      if (currentActiveTab === tabId) {
        localStorage.removeItem(ACTIVE_TAB_ID_KEY);
        localStorage.removeItem(HEARTBEAT_KEY);
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', onStorageChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  return { isBlocked };
}
